import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Download, Calendar, User, Loader2, AlertCircle, FileText, Image, Video, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { BizouLogo, KissIcon } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";
import { LoginDialog } from "@/components/LoginDialog";
import { pixel } from "@/lib/pixel";

export default function MessageView({ user }) {
  const { token } = useParams();
  const location = useLocation();
  const { t, lang } = useTranslation();
  const dateLocale = lang === "fr" ? frLocale : undefined;
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [wrongEmail, setWrongEmail] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => { fetchMessage(); }, [token, user]);

  const fetchMessage = async () => {
    setLoading(true);
    setError(null);
    setNeedsAuth(false);
    setWrongEmail(false);
    try {
      const response = await fetch(`${API}/view/${token}`, { credentials: "include" });
      if (response.status === 401) {
        setNeedsAuth(true);
        return;
      }
      if (response.status === 403) {
        try {
          const text = await response.text();
          const data = JSON.parse(text);
          if (data.detail && data.detail.includes("adresse email")) {
            setWrongEmail(true);
          } else {
            setError(data.detail || t("messageView.notAvailable"));
          }
        } catch {
          setWrongEmail(true);
        }
        return;
      }
      if (!response.ok) {
        setError(response.status === 404 ? t("messageView.messageNotFound") : t("messageView.errorOccurred"));
        return;
      }
      const text = await response.text();
      const msg = JSON.parse(text);
      setMessage(msg);
      pixel.viewContent({ messageId: msg.message_id, title: msg.title });
    } catch {
      setError(t("messageView.errorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    setShowLogin(true);
  };

  const handleLogoutAndRetry = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setShowLogin(true);
  };

  const getFileUrl = (f) => f.signed_url || `${API}/view/${token}/files/${f.file_id}`;

  const isVideo = (f) => f.file_type === "video" || f.file_name?.match(/\.(mp4|webm|mov|avi)$/i);
  const isImage = (f) => f.file_type === "photo" || f.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  if (needsAuth || (!user && !loading && !message && !error && !wrongEmail)) {
    return (
      <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center" data-testid="auth-required">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="w-20 h-20 rounded-full bg-[#E8175D]/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-[#E8175D]" strokeWidth={1.5} />
            </div>
            <BizouLogo size="lg" className="justify-center mb-4" />
            <h1 className="text-2xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("messageView.youReceived")}
            </h1>
            <p className="text-[#78716C] mb-6">
              {t("messageView.loginToRead")}
            </p>
            <Button onClick={handleLogin} className="w-full h-12 rounded-full bg-[#E8175D] text-white font-medium hover:bg-[#C4134F] shadow-lg" data-testid="login-to-view-btn">
              <Mail className="w-5 h-5 mr-2" />
              {t("common.login")}
            </Button>
          </div>
        </div>
        {showLogin && <LoginDialog onClose={() => setShowLogin(false)} redirectPath={window.location.pathname} />}
      </div>
    );
  }

  if (wrongEmail) {
    return (
      <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center" data-testid="wrong-email">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("messageView.wrongEmailTitle")}
            </h1>
            <p className="text-[#78716C] mb-2">
              {t("messageView.wrongEmailConnected")} <strong className="text-[#1C1917]">{user?.email}</strong>.
            </p>
            <p className="text-[#78716C] mb-6">
              {t("messageView.wrongEmailMsg")}
            </p>
            <Button onClick={handleLogoutAndRetry} className="w-full h-12 rounded-full bg-[#E8175D] text-white font-medium hover:bg-[#C4134F] shadow-lg" data-testid="switch-account-btn">
              {t("messageView.switchAccount")}
            </Button>
          </div>
        </div>
        {showLogin && <LoginDialog onClose={() => setShowLogin(false)} redirectPath={window.location.pathname} />}
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <KissIcon className="w-12 h-12 text-[#E8175D] mx-auto mb-4 kiss-pulse" />
        <Loader2 className="w-6 h-6 text-[#E8175D] mx-auto animate-spin" />
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h1 className="text-2xl font-medium text-[#1C1917] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{error}</h1>
        <a href="/" className="btn-primary inline-flex items-center">
          <KissIcon className="w-5 h-5 mr-2" />{t("messageView.discoverBizou")}
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDF0F4] py-6 px-4" data-testid="message-view">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <a href="/" className="inline-flex items-center"><BizouLogo size="md" /></a>
        </div>

        <div className="bg-white rounded-3xl shadow-lg overflow-hidden" data-testid="message-card">
          <div className="bg-[#E8175D]/5 p-6 border-b border-[#F0E8EB]">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#E8175D]/10 flex items-center justify-center">
                <User className="w-7 h-7 text-[#E8175D]" />
              </div>
              <div>
                <p className="text-sm text-[#78716C]">{t("messageView.sentBy")}</p>
                <p className="text-lg font-medium text-[#1C1917]" data-testid="sender-name">{message.sender_name}</p>
              </div>
              <KissIcon className="w-8 h-8 text-[#E8175D] ml-auto kiss-pulse" />
            </div>
          </div>

          <div className="p-6 md:p-8">
            {message.title && (
              <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="message-title">
                {message.title}
              </h1>
            )}

            <p className="text-lg text-[#78716C] mb-4 italic" data-testid="recipient-greeting">
              {t("messageView.greeting", { name: message.recipient_name })}
            </p>

            <div className="text-[#1C1917] text-base md:text-lg leading-relaxed whitespace-pre-wrap mb-8" data-testid="message-content">
              {message.content}
            </div>

            {message.files && message.files.length > 0 && (
              <div className="space-y-4 mb-6" data-testid="files-section">
                {message.files.map((f) => (
                  <div key={f.file_id} data-testid={`file-${f.file_id}`}>
                    {isVideo(f) ? (
                      <div className="rounded-2xl overflow-hidden bg-[#1C1917]">
                        <video controls playsInline preload="metadata" className="w-full aspect-video" data-testid={`video-player-${f.file_id}`}>
                          <source src={getFileUrl(f)} />
                          {t("messageView.videoNotSupported")}
                        </video>
                        <div className="flex items-center justify-between p-3 bg-[#1C1917] text-white/70 text-sm">
                          <span className="flex items-center gap-2"><Video className="w-4 h-4" />{f.file_name}</span>
                          <a href={getFileUrl(f)} download className="flex items-center gap-1 text-[#E8175D] hover:text-[#C4134F]">
                            <Download className="w-4 h-4" />{t("messageView.download")}
                          </a>
                        </div>
                      </div>
                    ) : isImage(f) ? (
                      <div className="rounded-2xl overflow-hidden">
                        <img src={getFileUrl(f)} alt={f.file_name} className="w-full rounded-2xl" data-testid={`image-${f.file_id}`} />
                        <div className="flex items-center justify-between mt-2 text-sm text-[#78716C]">
                          <span className="flex items-center gap-2"><Image className="w-4 h-4" />{f.file_name}</span>
                          <a href={getFileUrl(f)} download className="flex items-center gap-1 text-[#E8175D] hover:text-[#C4134F]">
                            <Download className="w-4 h-4" />{t("messageView.download")}
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4 p-4 bg-[#FDF0F4] rounded-2xl">
                        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#E8175D]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1C1917] truncate">{f.file_name}</p>
                          <p className="text-sm text-[#78716C]">{t("messageView.attachedFile")}</p>
                        </div>
                        <a href={getFileUrl(f)} download>
                          <Button className="btn-primary h-10 px-4" data-testid={`download-${f.file_id}`}>
                            <Download className="w-4 h-4 mr-2" />{t("messageView.open")}
                          </Button>
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {message.sent_at && (
              <div className="flex items-center gap-2 text-sm text-[#78716C] pt-6 border-t border-[#F0E8EB]">
                <Calendar className="w-4 h-4" />
                <span>{t("messageView.receivedOn", { date: format(new Date(message.sent_at), "d MMMM yyyy", { locale: dateLocale }) })}</span>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-10">
          <p className="text-[#78716C] mb-4">{t("messageView.sendBizouToo")}</p>
          <a href="/" className="btn-primary inline-flex items-center" data-testid="discover-btn">
            <KissIcon className="w-5 h-5 mr-2" />{t("messageView.createMyBizou")}
          </a>
        </div>
      </div>
    </div>
  );
}
